"use client";

import React, { createContext, useContext, useState, useCallback, useRef, type ReactNode } from "react";
import type { FilterKey } from "@/lib/filters/types";
import { FilterRequestDto } from "@/lib/api/apiwrapper";

// ── Types ────────────────────────────────────────────────────

interface PendingChange {
  field: FilterKey;
  value: unknown;
  label: string; // human-readable description, e.g. "Year: 2024"
}

interface GlobalFilterContextType {
  /** Current global filters (only year/dateRange make sense as global) */
  globalFilters: Pick<FilterRequestDto, "year" | "dateRange">;
  /** Directly set a global filter value */
  setGlobalFilter: <K extends "year" | "dateRange">(key: K, value: FilterRequestDto[K]) => void;
  /** Propose a change that pages can accept/dismiss */
  proposeGlobalChange: (field: FilterKey, value: unknown, label: string) => void;
  /** Current pending change (if any) */
  pendingChange: PendingChange | null;
  /** Accept the pending change → applies it to global state */
  acceptPendingChange: () => void;
  /** Dismiss the pending change without applying */
  dismissPendingChange: () => void;
  /** Pages call this on mount to signal they want proposals instead of direct applies */
  registerPageListener: (pageId: string) => () => void;
}

const GlobalFilterContext = createContext<GlobalFilterContextType | undefined>(undefined);

// ── Provider ─────────────────────────────────────────────────

export function GlobalFilterProvider({ children }: { children: ReactNode }) {
  const [globalFilters, setGlobalFilters] = useState<Pick<FilterRequestDto, "year" | "dateRange">>({
    year: null,
  });
  const [pendingChange, setPendingChange] = useState<PendingChange | null>(null);
  const pageListenersRef = useRef<Set<string>>(new Set());

  const setGlobalFilter = useCallback(<K extends "year" | "dateRange">(key: K, value: FilterRequestDto[K]) => {
    setGlobalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const proposeGlobalChange = useCallback((field: FilterKey, value: unknown, label: string) => {
    if (pageListenersRef.current.size === 0) {
      // No page is listening → apply immediately
      setGlobalFilters((prev) => ({ ...prev, [field]: value }));
    } else {
      setPendingChange({ field, value, label });
    }
  }, []);

  const acceptPendingChange = useCallback(() => {
    if (pendingChange) {
      setGlobalFilters((prev) => ({ ...prev, [pendingChange.field]: pendingChange.value }));
      setPendingChange(null);
    }
  }, [pendingChange]);

  const dismissPendingChange = useCallback(() => {
    setPendingChange(null);
  }, []);

  const registerPageListener = useCallback((pageId: string) => {
    pageListenersRef.current.add(pageId);
    return () => {
      pageListenersRef.current.delete(pageId);
    };
  }, []);

  return (
    <GlobalFilterContext.Provider
      value={{
        globalFilters,
        setGlobalFilter,
        proposeGlobalChange,
        pendingChange,
        acceptPendingChange,
        dismissPendingChange,
        registerPageListener,
      }}
    >
      {children}
    </GlobalFilterContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────

export function useGlobalFilters() {
  const context = useContext(GlobalFilterContext);
  if (!context) {
    throw new Error("useGlobalFilters must be used within a GlobalFilterProvider");
  }
  return context;
}
